import { Component, OnInit, Input, ViewChild, ElementRef, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Global } from "../classes/Global";
import { Router } from "@angular/router";
import { BaseComponent } from '../classes/BaseComponent';
import { PriceHelper } from '../helpers/PriceHelper';
import { ListHelper } from '../helpers/ListHelper';
import { Config } from '../classes/Config';
import { WishlistService } from '../wishlist.service';
import { DateHelper } from '../helpers/DateHelper';
import { StringHelper } from '../helpers/StringHelper';

@Component({
  selector: 'app-grid-item-wishlist',
  templateUrl: './grid-item-wishlist.component.html',
  styleUrls: ['./grid-item-wishlist.component.scss'],
  providers: [ WishlistService ]
})
export class GridItemWishlistComponent extends BaseComponent implements OnInit, OnChanges {

  id;
  highResImageLoaded = false;
  showPlaceHolder: boolean = false;

  imageContainerClass: string;
  imageClass: string;
  footerClass: string;
  gridItemClass: string;
  detailViewUrl: string;

  // Cached list states - updated only when item changes
  inOwnlist: boolean = false;
  inBlocklist: boolean = false;

  @Input() item;
  @Input() priceHint;
  @Input() store: string;
  @Output() onItemClicked = new EventEmitter();
  @Output() onRemoveFromWishlistClicked  = new EventEmitter();

  constructor(
    private router: Router
  ) {
    super();
  }

  ngOnInit() {
    if (this.item) {
      this.id = this.item.idInStore || this.item.itemID;

      if (this.id) {
        let media = Global.getMediaTypeForDetailView();
        const country = Global.getCountry();
        const storeUrl = Global.getStoreUrl();
        const mediaUrl = this.getString(media + 'Url');
        const title = this.item.title;

        if (country && storeUrl && mediaUrl && title) {
          this.detailViewUrl = '/' + country + '/' + storeUrl + '/' + mediaUrl + '/' + this.id + '/' + StringHelper.slugify(title);
        } else {
          this.detailViewUrl = "";
        }
      } else {
        this.detailViewUrl = "";
      }

      this.gridItemClass = this.getGridItemClass();
      this.imageContainerClass = this.getImageContainerClass();
      this.imageClass = this.getImageClass();
      this.footerClass = this.getFooterClass();
      this.updateListStates();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.item && changes.item.currentValue) {
      this.updateListStates();
    }
  }

  /** Updates cached list states - call this when item changes */
  updateListStates(): void {
    if (!this.item) {
      this.inOwnlist = false;
      this.inBlocklist = false;
      return;
    }
    this.inOwnlist = ListHelper.isItemInOwnlist(this.item);
    this.inBlocklist = ListHelper.isItemInBlocklist(this.item, this.store);
  }

  getItemImageUrl(): string {
    if (this.id) {
      return Config.imageServerUrl + Global.getCountry() + "/" + this.id + "/400x400.jpg";
    }
    return "";
  }

  getHttpsImageUrl() {
    const url = this.item.imageLargeUrl || this.item.itemImageURL || this.item.imageMediumUrl || this.item.imageSmallUrl || '';
    return StringHelper.getImageHttpsUrl(url);
  }

  isInWishlist(): boolean {
    return ListHelper.isItemInWishlist(this.item);
  }

  /** @deprecated Use inOwnlist property instead for template bindings */
  isInOwnlist(): boolean {
    return this.inOwnlist;
  }

  /** @deprecated Use inBlocklist property instead for template bindings */
  isInBlocklist(): boolean {
    return this.inBlocklist;
  }

  hasRectangleImage(): boolean {
    // Seasons are squared images on iTunes, but rectangle images on Vudu
    if (this.item.itemType == "season" && this.store == "vudu") {
      return true; // Use rectangle image for landscape seasons on Vudu
    }
    return this.item.itemType == "movie" || this.item.itemType == "ebook";
  }

  formatPrice(price: number, currency: string): string {
    if (price && currency) {
      return PriceHelper.formatPrice(price, currency);
    }
    else {
      return "";
    }
  }

  removeFromWishlist(e) {
    e.stopPropagation();
    e.preventDefault();
    this.onRemoveFromWishlistClicked.emit();
    return false;
  }

  urlIsWishlist(): boolean {
    return this.router.url.indexOf("wishlist") > -1;
  }

  urlIsHomePage(): boolean {
    return this.router.url == "/";
  }

  getImageContainerClass(): string {
    let classString =  this.hasRectangleImage() ? this.getRectangleImageClass() : this.getSquareImageClass();
    classString += " image-container";
    return classString;
  }

  getRectangleImageClass(): string {
    return 'rectangle-image';
  }

  getSquareImageClass(): string {
    return 'square-image';
  }

  getImageClass(): string {
    let classString = 'item-image ';
    if (this.hasRectangleImage()) {
      classString += this.getRectangleImageClass();
    }
    else {
      classString += this.getSquareImageClass();
    }
    if (this.item.itemType == "app") {
      classString += " rounded-top-border";
    }
    return classString;
  }

  getFooterClass(): string {
    let classString = 'grid-item-footer';

    const priceStart = this.getStartPrice();
    const priceNow = this.item.price || this.item.priceHd || this.item.priceSd || this.item.rentalPriceHd || this.item.rentalPriceSd;
    if (priceNow < priceStart) {
      classString += " green-background";
    }

    return classString;
  }

  getGridItemClass(): string {
    let classString = 'grid-item';
    if (this.item.itemType == "app") {
      classString += " rounded-top-border";
    }
    return classString;
  }

  useIvan(): boolean {
    return Config.useIvan;
  }

  storeIsItunes(): boolean {
    return this.store == Global.ITUNES;
  }

  getTimeAgoString(): string {

    if (!this.item.wishlistInfo) {
      return "";
    }

    const date: Date = DateHelper.dateStringToDateObject(this.item.wishlistInfo.addedToWishlistAt);
    if (!date) {
      return "";
    }

    const timeAgo = DateHelper.getTimeAgo(date, "day");
    if (!timeAgo) {
      return "";
    }

    if (timeAgo["text"]) {
      return StringHelper.lowCaseFirstLetter(this.getString(timeAgo["text"]));
    }

    return StringHelper.format(this.getString("timeAgo"), timeAgo.value, this.getString(timeAgo.unit));

  }

  getStartPrice(): number {
    let startPrice;
    if (this.item && this.item.wishlistInfo) {
      if (this.item.wishlistInfo.isMarkedAsBuyItem == true) {
        if (this.item.wishlistInfo.initialBuyHdPriceValue &&
          this.item.wishlistInfo.initialBuyHdPriceValue != -1) {
          startPrice = this.item.wishlistInfo.initialBuyHdPriceValue;
        }
        else {
          startPrice = this.item.wishlistInfo.initialBuySdPriceValue;
        }
      }
      else {
        if (this.item.wishlistInfo.initialRentalHdPriceValue &&
          this.item.wishlistInfo.initialRentalHdPriceValue != -1) {
          startPrice = this.item.wishlistInfo.initialRentalHdPriceValue;
        }
        else {
          startPrice = this.item.wishlistInfo.initialRentalSdPriceValue;
        }

      }
    }

    return startPrice;
  }

  getStartPriceAsString(): string {

    const startPrice = this.getStartPrice();

    if (startPrice == 0) {
      return this.getString("free");
    }
    return this.formatPrice(startPrice, this.item.currency);
  }

  getAddedFor(): string {
    return StringHelper.format(this.getString("addedFor"), this.getTimeAgoString(), this.getStartPriceAsString());
  }

}
